import { FC, MouseEvent, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  BroadcastEventMapping,
  ProviderType,
  UserGroupEnum,
} from 'videoservice-openapi-react';
import { BindEvents } from 'features/broadcast/bind-event';
import { useRemoveEvent } from 'features/event';
import { EventDate, useEventStore } from 'entities/event';
import { useMatchTypesStore } from 'entities/match-type';
import { useDialogContext } from 'shared/lib/dialog';
import { useHasRole } from 'shared/lib/role';
import { EElementSize } from 'shared/types';
import { AddIcon, Button, CloseIcon, Date, EButtonType } from 'shared/ui';
import { MappedEventsCellStyled } from './mapped-events-cell.styled';
import { IMappedEventsCellProps } from './mapped-events-cell.types';

const EventMatchType: FC<{ eventId: number }> = ({ eventId }) => {
  const eventsMap = useEventStore(state => state.map);
  const matchTypesMap = useMatchTypesStore(state => state.map);

  const event = useMemo(() => eventsMap.get(eventId), [eventId, eventsMap]);

  const eventMatchType = useMemo(() => {
    if (event && event.eventTypeId) {
      return matchTypesMap.get(event.eventTypeId);
    }
  }, [event, matchTypesMap]);

  return <>{eventMatchType && `(${eventMatchType.name})`}</>;
};

export const MappedEventsCell: FC<IMappedEventsCellProps> = ({ broadcast }) => {
  const { t } = useTranslation();
  const { removeEventMutation } = useRemoveEvent(broadcast);
  const { hasRole } = useHasRole();
  const { openDialog } = useDialogContext();

  const closeHandler = async (e: MouseEvent, event: BroadcastEventMapping) => {
    e.stopPropagation();

    try {
      await openDialog({
        title: 'Отвязать событие',
        description: event.description,
      });

      removeEventMutation.mutate(event.sbSportEventId);
    } catch (error) {
      // Cancel remove mapping
    }
  };

  const canMap = useMemo(() => {
    return (
      (hasRole(UserGroupEnum.OPERATOR, UserGroupEnum.ADMIN) &&
        broadcast.providerType !== ProviderType.MANUAL) ||
      (hasRole(UserGroupEnum.CUSTOMER) &&
        broadcast.providerType === ProviderType.MANUAL)
    );
  }, [broadcast.providerType, hasRole]);

  return (
    <MappedEventsCellStyled onClick={e => e.stopPropagation()}>
      {broadcast.mappings && broadcast.mappings.length > 0 && (
        <div className="events">
          {broadcast.mappings.map(event => (
            <div className="event" key={event.sbSportEventId}>
              <div className="info">
                <span>
                  <EventDate id={event.sbSportEventId} />
                  {event.description}{' '}
                  <EventMatchType eventId={event.sbSportEventId} /> [
                  {event.sbSportEventId}]
                </span>
                {event.mappingAt && (
                  <div className="date">
                    <Date date={event.mappingAt} pattern="dd.MM.yyyy HH:mm" />
                  </div>
                )}
              </div>
              {/* TODO: will be placed to features */}
              {canMap && (
                <Button
                  $type={EButtonType.GHOST_GRAY}
                  $size={EElementSize.S}
                  prependIcon={<CloseIcon />}
                  onClick={e => closeHandler(e, event)}
                />
              )}
            </div>
          ))}
        </div>
      )}
      {canMap && (
        <div className="actions">
          <BindEvents broadcast={broadcast} />

          {/* TODO: will be placed to features */}
          <Button
            $type={EButtonType.GHOST_GRAY}
            $size={EElementSize.S}
            prependIcon={<AddIcon />}
            disabled
          >
            {t('mapping')}
          </Button>
        </div>
      )}
    </MappedEventsCellStyled>
  );
};
